import React from "react";
import {
    FormControl,
    Grid,
    InputAdornment,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { DesiredCompensationBasis } from "../../models/worker";
import { WorkerFormState } from "./workerDetailForm";

type PreferenceKey =
    | "willingToWorkInNetherlands"
    | "housingNeeded"
    | "ownTransport"
    | "desiredCompensationBasis"
    | "desiredCompensationAmount"
    | "originCountry"
    | "originCity";

interface RecruitmentPreferencesSectionProps {
    value: WorkerFormState;
    editMode: boolean;
    onChange: <K extends PreferenceKey>(key: K, value: WorkerFormState[K]) => void;
}

const BOOLEAN_OPTIONS = [
    { value: "", label: "Unknown" },
    { value: "true", label: "Yes" },
    { value: "false", label: "No" },
] as const;

const COMPENSATION_OPTIONS: Array<{ value: DesiredCompensationBasis; label: string }> = [
    { value: "GROSS_HOURLY", label: "Gross hourly salary" },
    { value: "GROSS_MONTHLY", label: "Gross monthly salary" },
    { value: "ZZP_HOURLY", label: "Self-employed hourly rate" },
];

const booleanValue = (value: boolean | null): string => value === null ? "" : String(value);
const parseBoolean = (value: string): boolean | null => value === "" ? null : value === "true";

const BooleanPreference = ({
    label,
    value,
    editMode,
    onChange,
}: {
    label: string;
    value: boolean | null;
    editMode: boolean;
    onChange: (value: boolean | null) => void;
}) => editMode ? (
    <FormControl fullWidth size="small">
        <InputLabel>{label}</InputLabel>
        <Select label={label} value={booleanValue(value)} onChange={(event) => onChange(parseBoolean(event.target.value))}>
            {BOOLEAN_OPTIONS.map((option) => (
                <MenuItem key={option.value || "unknown"} value={option.value}>{option.label}</MenuItem>
            ))}
        </Select>
    </FormControl>
) : (
    <TextField
        label={label}
        value={BOOLEAN_OPTIONS.find((option) => option.value === booleanValue(value))?.label ?? "Unknown"}
        fullWidth
        size="small"
        InputProps={{ readOnly: true }}
    />
);

export const RecruitmentPreferencesSection: React.FC<RecruitmentPreferencesSectionProps> = ({
    value,
    editMode,
    onChange,
}) => {
    const theme = useTheme();

    return (
        <Paper
            variant="outlined"
            sx={{
                p: 2,
                borderRadius: 2,
                borderColor: alpha(theme.palette.divider, theme.palette.mode === "dark" ? 0.4 : 1),
            }}
        >
            <Typography variant="subtitle1" fontWeight={900}>Recruitment preferences</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Origin, mobility, housing and desired compensation.
            </Typography>

            <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                    <TextField
                        label="Country of origin"
                        value={value.originCountry}
                        onChange={(event) => onChange("originCountry", event.target.value)}
                        fullWidth
                        size="small"
                        InputProps={{ readOnly: !editMode }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField
                        label="City of origin"
                        value={value.originCity}
                        onChange={(event) => onChange("originCity", event.target.value)}
                        fullWidth
                        size="small"
                        InputProps={{ readOnly: !editMode }}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <BooleanPreference
                        label="Willing to work in NL"
                        value={value.willingToWorkInNetherlands}
                        editMode={editMode}
                        onChange={(next) => onChange("willingToWorkInNetherlands", next)}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <BooleanPreference
                        label="Housing needed"
                        value={value.housingNeeded}
                        editMode={editMode}
                        onChange={(next) => onChange("housingNeeded", next)}
                    />
                </Grid>
                <Grid item xs={12} sm={4}>
                    <BooleanPreference
                        label="Own transport"
                        value={value.ownTransport}
                        editMode={editMode}
                        onChange={(next) => onChange("ownTransport", next)}
                    />
                </Grid>
                <Grid item xs={12} sm={7}>
                    {editMode ? (
                        <FormControl fullWidth size="small">
                            <InputLabel>Compensation basis</InputLabel>
                            <Select
                                label="Compensation basis"
                                value={value.desiredCompensationBasis ?? ""}
                                onChange={(event) => onChange(
                                    "desiredCompensationBasis",
                                    (event.target.value || null) as DesiredCompensationBasis | null,
                                )}
                            >
                                <MenuItem value="">Not specified</MenuItem>
                                {COMPENSATION_OPTIONS.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    ) : (
                        <TextField
                            label="Compensation basis"
                            value={COMPENSATION_OPTIONS.find((option) => option.value === value.desiredCompensationBasis)?.label ?? "Not specified"}
                            fullWidth
                            size="small"
                            InputProps={{ readOnly: true }}
                        />
                    )}
                </Grid>
                <Grid item xs={12} sm={5}>
                    <TextField
                        label="Desired amount"
                        type={editMode ? "number" : "text"}
                        value={value.desiredCompensationAmount ?? ""}
                        onChange={(event) => onChange(
                            "desiredCompensationAmount",
                            event.target.value === "" ? null : Number(event.target.value),
                        )}
                        fullWidth
                        size="small"
                        inputProps={{ min: 0, step: 0.01 }}
                        InputProps={{
                            readOnly: !editMode,
                            startAdornment: <InputAdornment position="start">EUR</InputAdornment>,
                        }}
                    />
                </Grid>
            </Grid>
        </Paper>
    );
};
